import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Navbar } from './components/Navbar';
import { AutoLogout } from './components/AutoLogout';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { CurrencyProvider } from './utils/CurrencyContext';
import LandingPage from './pages/LandingPage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import StudentDashboard from './pages/StudentDashboard';
import StudentProfilePage from './pages/StudentProfilePage';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherPaymentSettings from './pages/TeacherPaymentSettings';
import AdminDashboard from './pages/AdminDashboard';
import LandingSlotManager from './pages/LandingSlotManager';
import CourseManagePage from './pages/CourseManagePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LegalPage from './pages/LegalPage';
import ChatPage from './pages/ChatPage';
import TeacherProfilePage from './pages/TeacherProfilePage';
import LessonViewPage from './pages/LessonViewPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MarketplacePage from './pages/MarketplacePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import TeacherProductsPage from './pages/TeacherProductsPage';
import TeacherAnnouncementsPage from './pages/TeacherAnnouncementsPage';
import TeacherGalleryPage from './pages/TeacherGalleryPage';
import LiveSessionPage from './pages/LiveSessionPage';
import SellerDashboard from './pages/SellerDashboard';
import SellerProfilePage from './pages/SellerProfilePage';
import PayPalReturnPage from './pages/PayPalReturnPage';
import GalleryPage from './pages/GalleryPage';
import { isAuthenticated, isTeacher, isStudent, isAdmin, isSeller } from './utils/auth';
import '@/App.css';

const ProtectedRoute = ({ children, requireTeacher, requireStudent, requireAdmin, requireSeller }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  
  // Admin is a superuser - can access any protected route
  if (isAdmin()) {
    return children;
  }
  
  if (requireAdmin) {
    // Non-admin trying to access admin route
    return <Navigate to="/" replace />;
  }
  
  if (requireTeacher && !isTeacher()) {
    return <Navigate to="/student/dashboard" replace />;
  }
  
  if (requireStudent && !isStudent() && !isSeller()) {
    return <Navigate to="/teacher/dashboard" replace />;
  }
  
  if (requireSeller && !isSeller()) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App">
      <CurrencyProvider>
        <BrowserRouter>
          <AutoLogout>
            <ImpersonationBanner />
            <Navbar />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:courseId" element={<CourseDetailPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/terms" element={<LegalPage />} />
              <Route path="/privacy" element={<LegalPage />} />
              <Route path="/terms/:role" element={<LegalPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/paypal-return" element={<PayPalReturnPage />} />
              <Route path="/teacher/:teacherId/gallery" element={<GalleryPage />} />
              
              <Route
                path="/student/dashboard"
                element={
                  <ProtectedRoute requireStudent>
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/seller/dashboard"
                element={
                  <ProtectedRoute requireSeller>
                    <SellerDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/seller/products"
                element={
                  <ProtectedRoute requireSeller>
                    <TeacherProductsPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/seller/profile"
                element={
                  <ProtectedRoute requireSeller>
                    <SellerProfilePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/student/profile"
                element={
                  <ProtectedRoute requireStudent>
                    <StudentProfilePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/learn/:courseId/lesson/:lessonId"
                element={
                  <ProtectedRoute requireStudent>
                    <LessonViewPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/dashboard"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/courses/:courseId/manage"
                element={
                  <ProtectedRoute requireTeacher>
                    <CourseManagePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/payment-settings"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherPaymentSettings />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/profile"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherProfilePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/announcements"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherAnnouncementsPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teacher/gallery"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherGalleryPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/admin/landing-slots"
                element={
                  <ProtectedRoute requireAdmin>
                    <LandingSlotManager />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <ChatPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/chat/:conversationId"
                element={
                  <ProtectedRoute>
                    <ChatPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Marketplace Routes */}
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/marketplace/product/:productId" element={<ProductDetailPage />} />
              <Route
                path="/marketplace/cart"
                element={
                  <ProtectedRoute>
                    <CartPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marketplace/checkout/success"
                element={
                  <ProtectedRoute>
                    <CheckoutSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/products"
                element={
                  <ProtectedRoute requireTeacher>
                    <TeacherProductsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Live Sessions */}
              <Route
                path="/live-session/:sessionId"
                element={
                  <ProtectedRoute>
                    <LiveSessionPage />
                  </ProtectedRoute>
                }
              />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" />
          </AutoLogout>
        </BrowserRouter>
      </CurrencyProvider>
    </div>
  );
}

export default App;